/**
 * 로그 화면의 순수 모델: 항목 타입, 필터(URL 쿼리와 양방향), 실시간 병합, 라벨.
 * 필터 규칙은 Rust `LogQuery::matches`와 같다. 과거 페이지는 Rust가, 실시간 배치는 여기서 같은 규칙으로 거른다.
 */

export type LogLevel = "debug" | "info" | "warn" | "error";
export type LogKind = "tool" | "bridge" | "session" | "app" | "storage" | "file" | "error";
export type LogStatus = "ok" | "fail" | "denied" | "timeout" | "busy";
export type LogSurface = "mcp_stdio" | "bridge_http" | "webview" | "app";
export type LogGroup = "all" | "app" | "mcp";
export type LogPeriod = "all" | "1h" | "24h" | "7d";

export interface LogEntry {
  id: string;
  seq: number;
  session: string;
  ts: string;
  level: LogLevel;
  type: LogKind;
  surface: LogSurface;
  action: string;
  crud: "read" | "write" | "other";
  peer: string | null;
  summary: string;
  durationMs: number | null;
  status: LogStatus;
  source?: string;
  meta: Record<string, unknown>;
}

export interface LogPage {
  items: LogEntry[];
  nextBefore: string | null;
  total: number;
  matched: number;
  corruptLines: number;
  enabled: boolean;
  writeError: string | null;
}

export interface LogBatch {
  items: LogEntry[];
  dropped: number;
}

export interface LogFilter {
  group: LogGroup;
  minLevel: LogLevel | "";
  status: LogStatus | "";
  action: string;
  text: string;
  period: LogPeriod;
}

export const EMPTY_FILTER: LogFilter = { group: "all", minLevel: "", status: "", action: "", text: "", period: "all" };

export const LEVEL_LABEL: Record<LogLevel, string> = { debug: "디버그", info: "정보", warn: "경고", error: "오류" };
export const KIND_LABEL: Record<LogKind, string> = { tool: "도구", bridge: "브리지", session: "세션", app: "앱", storage: "저장", file: "파일", error: "오류" };
export const STATUS_LABEL: Record<LogStatus, string> = { ok: "성공", fail: "실패", denied: "거부", timeout: "시간 초과", busy: "혼잡" };
export const SURFACE_LABEL: Record<LogSurface, string> = { mcp_stdio: "MCP", bridge_http: "브리지", webview: "앱 화면", app: "앱" };
export const GROUP_LABEL: Record<LogGroup, string> = { all: "전체", app: "앱", mcp: "MCP" };
export const PERIOD_LABEL: Record<LogPeriod, string> = { all: "모든 기간", "1h": "최근 1시간", "24h": "최근 24시간", "7d": "최근 7일" };

const LEVEL_ORDER: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 };
const MCP_KINDS: ReadonlySet<LogKind> = new Set(["tool", "bridge", "session"]);
const PERIOD_MS: Record<Exclude<LogPeriod, "all">, number> = { "1h": 3_600_000, "24h": 86_400_000, "7d": 7 * 86_400_000 };

const isLevel = (v: string | null): v is LogLevel => v === "debug" || v === "info" || v === "warn" || v === "error";
const isStatus = (v: string | null): v is LogStatus => v === "ok" || v === "fail" || v === "denied" || v === "timeout" || v === "busy";
const isGroup = (v: string | null): v is LogGroup => v === "all" || v === "app" || v === "mcp";
const isPeriod = (v: string | null): v is LogPeriod => v === "all" || v === "1h" || v === "24h" || v === "7d";

export function sinceOf(period: LogPeriod, now: number): string | undefined {
  return period === "all" ? undefined : new Date(now - PERIOD_MS[period]).toISOString();
}

/** Rust `LogQuery::matches`와 같은 규칙. */
export function matchesFilter(e: LogEntry, f: LogFilter, now: number): boolean {
  if (f.group === "mcp" && !MCP_KINDS.has(e.type)) return false;
  if (f.group === "app" && MCP_KINDS.has(e.type)) return false;
  if (f.minLevel && LEVEL_ORDER[e.level] < LEVEL_ORDER[f.minLevel]) return false;
  if (f.status && e.status !== f.status) return false;
  if (f.action && e.action !== f.action) return false;
  const since = sinceOf(f.period, now);
  if (since && e.ts < since) return false;
  const text = f.text.trim().toLowerCase();
  if (text && !`${e.summary} ${e.action} ${e.peer ?? ""}`.toLowerCase().includes(text)) return false;
  return true;
}

export function toQuery(f: LogFilter, now: number, extra: { before?: string; limit?: number } = {}) {
  return {
    group: f.group,
    minLevel: f.minLevel || undefined,
    status: f.status || undefined,
    action: f.action || undefined,
    text: f.text.trim() || undefined,
    since: sinceOf(f.period, now),
    ...extra,
  };
}

export function hasFilter(f: LogFilter): boolean {
  return f.group !== "all" || !!f.minLevel || !!f.status || !!f.action || !!f.text.trim() || f.period !== "all";
}

/** URL 쿼리 → 필터. 모르는 값은 버린다(새로고침·공유에 견딘다). */
export function parseFilter(params: URLSearchParams): LogFilter {
  const g = params.get("group");
  const l = params.get("level");
  const s = params.get("status");
  const p = params.get("period");
  return {
    group: isGroup(g) ? g : "all",
    minLevel: isLevel(l) ? l : "",
    status: isStatus(s) ? s : "",
    action: (params.get("tool") ?? "").slice(0, 100),
    text: (params.get("q") ?? "").slice(0, 200),
    period: isPeriod(p) ? p : "all",
  };
}

export function filterToParams(f: LogFilter): URLSearchParams {
  const out = new URLSearchParams();
  if (f.group !== "all") out.set("group", f.group);
  if (f.minLevel) out.set("level", f.minLevel);
  if (f.status) out.set("status", f.status);
  if (f.action) out.set("tool", f.action);
  if (f.text.trim()) out.set("q", f.text.trim());
  if (f.period !== "all") out.set("period", f.period);
  return out;
}

export const LIVE_CAP = 10_000;

/**
 * 목록 끝에 새 항목을 붙인다. id로 중복을 없애고(페이지와 이벤트가 겹쳐도 한 번만, ADV-G06)
 * `cap`을 넘으면 가장 오래된 것부터 버린다(메모리 상한, ADV-K02).
 */
export function appendUnique(list: readonly LogEntry[], incoming: readonly LogEntry[], cap = LIVE_CAP): LogEntry[] {
  if (!incoming.length) return list as LogEntry[];
  const seen = new Set(list.map((e) => e.id));
  const fresh = incoming.filter((e) => {
    if (seen.has(e.id)) return false;
    seen.add(e.id);
    return true;
  });
  if (!fresh.length) return list as LogEntry[];
  const next = [...list, ...fresh];
  return next.length > cap ? next.slice(next.length - cap) : next;
}

export function prependUnique(list: readonly LogEntry[], older: readonly LogEntry[]): LogEntry[] {
  const seen = new Set(list.map((e) => e.id));
  return [...older.filter((e) => !seen.has(e.id)), ...list];
}

/** 표에 쓰는 상대(peer) 표기. 없으면 표면 이름. */
export function peerLabel(e: LogEntry): string {
  return e.peer ?? SURFACE_LABEL[e.surface];
}

/** 도구별 호출 수·최근 호출 시각(MCP 연결 화면의 도구 목록). 플레이그라운드 호출은 빼고 외부 호출만 센다. */
export function toolUsage(entries: readonly LogEntry[]): Record<string, { count: number; lastAt: string }> {
  const out: Record<string, { count: number; lastAt: string }> = {};
  for (const e of entries) {
    if (e.type !== "tool" || e.surface === "webview") continue;
    const u = (out[e.action] ??= { count: 0, lastAt: e.ts });
    u.count++;
    if (e.ts > u.lastAt) u.lastAt = e.ts;
  }
  return out;
}

export function isProblem(e: Pick<LogEntry, "status" | "level">): boolean {
  return e.status !== "ok" || e.level === "error";
}
